import { IAccessTokedDecoded } from "dash-common";
import {
  Button,
  Form,
  FormErrorTypes,
  Input,
  jwtDecodeSafely,
  Modal,
  ModalContext,
  ViewerContext,
} from "dash-ui";
import { ChangeEvent, FormEvent, useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { useConfirmSsoName } from "../model/use-confirm-sso-name";

export const CONFIRM_SSO_NAME_MODAL = "ConfirmSsoNameModal";

interface ConfirmSsoNameFormProps {
  defaultFirstName: string;
  defaultLastName: string;
  onConfirmed: () => void;
}

function ConfirmSsoNameForm({
  defaultFirstName,
  defaultLastName,
  onConfirmed,
}: ConfirmSsoNameFormProps) {
  const { t } = useTranslation(["pages", "forms"]);
  const [firstName, setFirstName] = useState(defaultFirstName);
  const [lastName, setLastName] = useState(defaultLastName);
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");

  const { confirm, saving, error } = useConfirmSsoName({ onConfirmed });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();

    setFirstNameError(
      trimmedFirstName
        ? ""
        : t("pages:signUpValidation.firstNameRequired") || ""
    );
    setLastNameError(
      trimmedLastName ? "" : t("pages:signUpValidation.lastNameRequired") || ""
    );

    if (!trimmedFirstName || !trimmedLastName) return;

    confirm({ firstName: trimmedFirstName, lastName: trimmedLastName });
  };

  const onFirstNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFirstName(event.target.value);
    if (firstNameError) setFirstNameError("");
  };

  const onLastNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLastName(event.target.value);
    if (lastNameError) setLastNameError("");
  };

  return (
    <Form id="confirm-sso-name-form" onSubmit={onSubmit}>
      <h3 className="text-2xl font-[Montserrat] mb-4 text-center">
        {t("pages:confirmSsoNameModal.title")}
      </h3>
      <h4 className="text-slate-500 text-sm text-center">
        {t("pages:confirmSsoNameModal.subtitle")}
      </h4>

      <Form.Group mt={6}>
        <Form.Label>{t("pages:confirmSsoNameModal.firstName")}</Form.Label>
        <Input
          w="100%"
          autoComplete="given-name"
          autoFocus
          value={firstName}
          onChange={onFirstNameChange}
          $errorType={firstNameError ? FormErrorTypes.Invalid : undefined}
        />
        <Form.Feedback $errorType={FormErrorTypes.Invalid}>
          {firstNameError}
        </Form.Feedback>
      </Form.Group>

      <Form.Group mt={4}>
        <Form.Label>{t("pages:confirmSsoNameModal.lastName")}</Form.Label>
        <Input
          w="100%"
          autoComplete="family-name"
          value={lastName}
          onChange={onLastNameChange}
          $errorType={lastNameError ? FormErrorTypes.Invalid : undefined}
        />
        <Form.Feedback $errorType={FormErrorTypes.Invalid}>
          {lastNameError || error}
        </Form.Feedback>
      </Form.Group>

      <Form.Group mt={6}>
        <Button w="100%" type="submit" disabled={saving} isLoading={saving}>
          {t("pages:confirmSsoNameModal.continue")}
        </Button>
      </Form.Group>
    </Form>
  );
}

/**
 * Asks an SSO user to confirm the name their provider handed us (DSH-939).
 *
 * Providers are not obliged to return a surname, and the CRM used to invent one.
 * The gate opens only when a name is actually missing, so a complete profile
 * costs the user nothing.
 */
export function ConfirmSsoNameModal() {
  const { isOpenCurrent, toggleModal } = useContext(ModalContext);
  const {
    account,
    tokens: { accessToken },
  } = useContext(ViewerContext);

  const isOpen = !!isOpenCurrent[CONFIRM_SSO_NAME_MODAL];

  const { given_name, family_name }: IAccessTokedDecoded =
    jwtDecodeSafely(accessToken) ?? {};

  return (
    <Modal isOpen={isOpen} viewHierarchy={1} p={10} w="fit-content">
      {isOpen && (
        <ConfirmSsoNameForm
          defaultFirstName={account?.profile?.firstName || given_name || ""}
          defaultLastName={account?.profile?.lastName || family_name || ""}
          onConfirmed={() => toggleModal(false, CONFIRM_SSO_NAME_MODAL)}
        />
      )}
    </Modal>
  );
}
